/**
 * socketService.js
 *
 * Socket.io client for real-time order updates.
 * Connects to the CO-LAB backend and listens for order_update events.
 * Broadcasts order status changes between patient, collector and facility.
 */

import { io } from 'socket.io-client';
import Cookies from 'js-cookie';
import api from './api';

const API_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

let socket = null;
let socketReauthing = false;

/**
 * Connect to socket server with JWT token.
 */
export const connectSocket = () => {
  const token = Cookies.get('jwt');
  if (!token) return null;

  if (socket?.connected) return socket;

  socket = io(API_URL, {
    // Function form: socket.io calls it on every connection attempt, so a
    // reconnect always carries the newest access token.
    auth: (cb) => cb({ token: Cookies.get('jwt') || token }),
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 2000,
  });

  socket.on('connect', () => {
    console.log('[CO-LAB Socket] Connected:', socket.id);
  });

  socket.on('disconnect', () => {
    console.log('[CO-LAB Socket] Disconnected');
  });

  socket.on('connect_error', async (err) => {
    console.warn('[CO-LAB Socket] Connection error:', err.message);
    // The server rejects an expired token at the handshake and socket.io does
    // not retry that on its own. Make one authenticated call (the api client
    // refreshes the token on a 401), then reconnect with the fresh token.
    if (!/Authentication error/i.test(err?.message || '') || socketReauthing) return;
    socketReauthing = true;
    try {
      await api.get('/auth/profile');
      socket?.connect();
    } catch (e) {
      // session truly expired: the api client's 401 handling signs the user out
    } finally {
      socketReauthing = false;
    }
  });

  return socket;
};

/**
 * Disconnect socket.
 */
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

/**
 * Subscribe to order_update events.
 * @param {Function} callback - called with order update payload
 */
export const onOrderUpdate = (callback) => {
  if (!socket) connectSocket();
  socket?.on('order_update', callback);
};

/**
 * Unsubscribe from order_update events.
 */
export const offOrderUpdate = (callback) => {
  socket?.off('order_update', callback);
};

export const getSocket = () => socket;

export const onCollectorLocationUpdate = (callback) => {
  if (!socket) connectSocket();
  socket?.on('collector_location_update', callback);
};

export const offCollectorLocationUpdate = (callback) => {
  socket?.off('collector_location_update', callback);
};

/**
 * Join a facility chat room to receive real-time messages.
 */
export const joinChatRoom = (chatRequestId) => {
  if (!chatRequestId) return;
  if (!socket) connectSocket();
  socket?.emit('join_chat', { chatRequestId });
};

/**
 * Leave a facility chat room.
 */
export const leaveChatRoom = (chatRequestId) => {
  if (!chatRequestId) return;
  socket?.emit('leave_chat', { chatRequestId });
};

/**
 * Subscribe to new_message events (facility chat).
 */
export const onNewMessage = (callback) => {
  if (!socket) connectSocket();
  socket?.on('new_message', callback);
};

export const offNewMessage = (callback) => {
  socket?.off('new_message', callback);
};

/**
 * Join an admin support chat room to receive real-time messages.
 */
export const joinAdminChatRoom = (conversationId) => {
  if (!conversationId) return;
  if (!socket) connectSocket();
  socket?.emit('join_admin_chat', { conversationId });
};

/**
 * Leave an admin support chat room.
 */
export const leaveAdminChatRoom = (conversationId) => {
  if (!conversationId) return;
  socket?.emit('leave_admin_chat', { conversationId });
};

/**
 * Subscribe to admin_chat_message events (customer support chat).
 */
export const onAdminChatMessage = (callback) => {
  if (!socket) connectSocket();
  socket?.on('admin_chat_message', callback);
};

export const offAdminChatMessage = (callback) => {
  socket?.off('admin_chat_message', callback);
};

/**
 * Subscribe to chat_list_update events -- fired whenever a new message
 * arrives in any of the user's chats, so the conversation LIST can
 * refresh (updated preview/unread badge) even for chats that aren't
 * currently open.
 */
export const onChatListUpdate = (callback) => {
  if (!socket) connectSocket();
  socket?.on('chat_list_update', callback);
};

export const offChatListUpdate = (callback) => {
  socket?.off('chat_list_update', callback);
};
